import { Query, skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { TenantRead, getRanaApiV1 } from "@nens/rana/api-v1";
import { ApiError, OurUseQueryOptions } from "@nens/react-query-utils";
import { ThreediModel, getHCCApi } from "@nens/threedi/openapi-client";

import { modelIsBeingGenerated } from "../utils/model-is-being-generated";

import { useAllHccSchematisationsListQuery } from "./query-hcc-schematisation";
import { useTenantQuery } from "./query-tenant";
import { combineQueries } from "./utils/combine-queries";
import { convertThreediOrg } from "./utils/convert-threedi-org";
import { fetchAllPagesHcc } from "./utils/fetch-all-pages";

// SINGLE

const PENDING_MODEL_POLL_INTERVAL = 3000;

// The model of a revision. `null` when the revision has no (enabled) model.

export type ThreediModelArgs = {
  schematisationId: number | undefined;
  revisionId: number | undefined;
};

const getThreediModelQuery = ({
  schematisationId,
  revisionId,
}: ThreediModelArgs): OurUseQueryOptions<ThreediModel | null> => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["hcc-threedimodel", schematisationId, revisionId] as const,
    queryFn:
      schematisationId !== undefined && revisionId !== undefined
        ? () =>
            getHCCApi()
              .schematisationsRevisionsThreedimodels({
                id: revisionId,
                schematisationPk: schematisationId,
              })
              .then((models) => models.find((model) => !model.disabled) ?? null)
        : skipToken,
    // isValid is null while the model is still being generated; keep polling until it resolves.
    // The interval also starts when the query mounts with cached pending data, so no separate
    // refetchOnMount check is needed.
    refetchInterval: (query: Query<ThreediModel | null, ApiError, ThreediModel | null>) =>
      modelIsBeingGenerated(query.state.data) && PENDING_MODEL_POLL_INTERVAL,
  });
};

export const useThreediModelQuery = (args: ThreediModelArgs) =>
  useQuery(getThreediModelQuery(args));

// LIST

/**
 * We filter models by schematisations from the catalogue because schematisations have additional
 * filters applied (ranaProjectIdIsnull, archived, lastUpdatedIsnull) that ensure we only get
 * schematisations that should be displayed in the catalogue. Since the models API doesn't support
 * these filters, we fetch all models and then filter them to only include those with schematisations
 * that are in the catalogue.
 */

const getAllThreediModelsQuery = (
  tenant: TenantRead | undefined,
  { enabled }: { enabled: boolean }
) =>
  getRanaApiV1().getQueryOptions({
    queryKey: ["allThreedimodels", tenant?.id],
    queryFn: tenant
      ? async () => {
          let threediModels: ThreediModel[] = [];

          const getAllThreediModelsByOwner = async (uniqueId: string) => {
            const { results } = await fetchAllPagesHcc((args) =>
              getHCCApi().threedimodelsList({
                ...args,
                revisionSchematisationOwnerUniqueId: uniqueId,
              })
            );

            threediModels = threediModels.concat(results);
          };

          await Promise.all(
            tenant.threediOrganisations.map(convertThreediOrg).map(getAllThreediModelsByOwner)
          );
          return threediModels;
        }
      : skipToken,
    enabled,
  });

export const useAllThreediModelsQuery = ({ enabled } = { enabled: true }) => {
  const tenantQuery = useTenantQuery();
  const schematisationsQuery = useAllHccSchematisationsListQuery();
  const _modelsQuery = useQuery(getAllThreediModelsQuery(tenantQuery.data, { enabled }));

  const modelsQuery: typeof _modelsQuery = useMemo(() => {
    if (!_modelsQuery.isSuccess) {
      return _modelsQuery;
    }

    // Filter to only include models whose schematisations are in the catalogue (see file-level comment)
    const filteredModels = _modelsQuery.data.filter((model) => {
      return schematisationsQuery.data?.results.some(
        (schematisation) => model.schematisationId === schematisation.id
      );
    });
    return {
      ..._modelsQuery,
      data: filteredModels,
    };
  }, [_modelsQuery, schematisationsQuery.data?.results]);

  return combineQueries(modelsQuery, tenantQuery, schematisationsQuery);
};

// INVALIDATION

// Sets the threedimodel cache directly instead of refetching, e.g. after a create/delete mutation.
export const useUpdateThreediModelQuery = (args: ThreediModelArgs) => {
  const queryClient = useQueryClient();
  const tenantQuery = useTenantQuery();

  return (model: ThreediModel | null) => {
    queryClient.setQueryData<ThreediModel | null>(getThreediModelQuery(args).queryKey, model);

    const allModelsQueryKey = getAllThreediModelsQuery(tenantQuery.data, {
      enabled: true,
    }).queryKey;

    const allModels = queryClient.getQueryData<ThreediModel[]>(allModelsQueryKey);
    if (allModels) {
      const updatedModels = [
        ...allModels.filter(
          (m) => m.revisionId !== args.revisionId || m.schematisationId !== args.schematisationId
        ),
        ...(model ? [model] : []),
      ];

      queryClient.setQueryData<ThreediModel[]>(allModelsQueryKey, updatedModels);
    }
  };
};
